import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// INVENTORY DRONE — TOS easter egg, Flappy Bird variant.
// You're piloting an autonomous inventory drone scanning the container yard.
// Stacks of containers rise from the ground and hang from gantry cranes —
// thread the drone through the gaps. Tap to thrust the rotors; gravity does
// the rest. One bump and the drone goes down.

const W = 480
const H = 720

// Tuned to feel like Flappy Bird: gravity strong enough that you can't coast,
// flap impulse big enough to recover, but inputs have to be deliberate.
const GRAVITY = 0.34
const FLAP_IMPULSE = -7.2
const MAX_FALL_SPEED = 11
const MAX_RISE_SPEED = -10
const SCROLL_SPEED = 2.4

const DRONE_X = W * 0.3
const DRONE_W = 38
const DRONE_H = 18

const OBSTACLE_W = 56
const OBSTACLE_GAP_MIN = 145
const OBSTACLE_GAP_MAX = 175
const OBSTACLE_SPACING_MIN = 240
const OBSTACLE_SPACING_MAX = 290
const GAP_TOP_MIN = 110
const GAP_TOP_MAX = 480

const HORIZON_Y = 540
const GROUND_Y = H - 30
const CEILING_DEATH_Y = -20

const SCORE_PER_PAIR = 1

const COMPANY_NAME = 'TERMINAL OS'

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext }

interface Drone {
  x: number
  y: number
  vy: number
  rotation: number
  dead: boolean
  deathFrames: number
  rotorBoost: number
  rotorPhase: number
}

interface Obstacle {
  x: number
  gapTop: number
  gapBottom: number
  width: number
  passed: boolean
  cargoColorIdx: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

type GameStatus = 'ready' | 'playing' | 'gameover'
type DeathCause = 'thud' | 'splash'

interface GameState {
  drone: Drone
  obstacles: Obstacle[]
  score: number
  hi: number
  status: GameStatus
  time: number
  bgPhase: number
  floaters: Floater[]
  particles: Particle[]
  shake: number
}

interface HudState {
  score: number
  hi: number
  status: GameStatus
  muted: boolean
}

interface InventoryDroneProps {
  onClose?: () => void
}

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function randInt(min: number, max: number): number {
  return Math.floor(rand(min, max + 1))
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init(): void {
    if (this.ctx) return
    try {
      const w = window as WindowWithWebkitAudio
      const Ctor = window.AudioContext || w.webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // Audio is non-essential; ignore unavailable AudioContext.
    }
  }

  private envelope(node: GainNode, attack: number, decay: number, peak = 1): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }

  flap(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(180, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(360, this.ctx.currentTime + 0.06)
    this.envelope(gain, 0.001, 0.07, 0.1)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.08)
  }

  score(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(1320, this.ctx.currentTime + 0.1)
    this.envelope(gain, 0.001, 0.12, 0.14)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.13)
  }

  thud(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.3
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 350
    const gain = this.ctx.createGain()
    this.envelope(gain, 0.003, dur, 0.45)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
    const osc = this.ctx.createOscillator()
    const g2 = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(150, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.25)
    this.envelope(g2, 0.005, 0.3, 0.35)
    osc.connect(g2).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.32)
  }

  splash(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.5
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    }
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'highpass'
    filter.frequency.value = 800
    const gain = this.ctx.createGain()
    this.envelope(gain, 0.005, dur, 0.25)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
  }

  fail(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(80, this.ctx.currentTime + 0.6)
    this.envelope(gain, 0.005, 0.6, 0.25)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.65)
  }

  toggleMute(): void {
    this.muted = !this.muted
  }
}

function makeObstaclePair(x: number): Obstacle {
  const gapHeight = rand(OBSTACLE_GAP_MIN, OBSTACLE_GAP_MAX)
  const gapTop = rand(GAP_TOP_MIN, GAP_TOP_MAX)
  return {
    x,
    gapTop,
    gapBottom: gapTop + gapHeight,
    width: OBSTACLE_W,
    passed: false,
    cargoColorIdx: randInt(0, 5),
  }
}

const InventoryDrone: FC<InventoryDroneProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const flapRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({ score: 0, hi: 0, status: 'ready', muted: false })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('inventoryDroneHi') || '0', 10) || 0
    } catch {
      // localStorage may be unavailable; ignore.
    }

    const initState = (): GameState => {
      // Pre-populate with 4 obstacle pairs so they're ready when the player starts.
      const obstacles: Obstacle[] = []
      let nextX = W + 100
      for (let i = 0; i < 4; i++) {
        obstacles.push(makeObstaclePair(nextX))
        nextX += rand(OBSTACLE_SPACING_MIN, OBSTACLE_SPACING_MAX)
      }
      return {
        drone: {
          x: DRONE_X,
          y: H * 0.4,
          vy: 0,
          rotation: 0,
          dead: false,
          deathFrames: 0,
          rotorBoost: 0,
          rotorPhase: 0,
        },
        obstacles,
        score: 0,
        hi: savedHi,
        status: 'ready',
        time: 0,
        bgPhase: 0,
        floaters: [],
        particles: [],
        shake: 0,
      }
    }

    stateRef.current = initState()

    const startGame = (): void => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud({ score: 0, hi: s.hi, status: 'playing', muted: audio.muted })
    }
    startGameRef.current = startGame

    const flap = (): void => {
      const s = stateRef.current
      if (!s) return
      if (s.status !== 'playing') return
      if (s.drone.dead) return
      s.drone.vy = FLAP_IMPULSE
      s.drone.rotorBoost = 8
      audio.flap()
      // Downwash dust particles below each rotor.
      for (let i = 0; i < 6; i++) {
        s.particles.push({
          x: s.drone.x + (i < 3 ? -16 : 16) + rand(-4, 4),
          y: s.drone.y + DRONE_H / 2 + rand(-1, 2),
          vx: rand(-1.5, -3),
          vy: rand(0.5, 2),
          life: rand(12, 22),
          color: 'rgba(200, 200, 210, 0.5)',
          size: rand(1.5, 2.5),
        })
      }
    }
    flapRef.current = flap

    const onKeyDown = (e: KeyboardEvent): void => {
      audio.init()
      if ([' ', 'ArrowUp'].includes(e.key)) e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (
        s.status === 'ready' &&
        (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W')
      ) {
        startGame()
        // Head-start flap so the drone doesn't immediately drop.
        setTimeout(() => flap(), 30)
        return
      }
      if (s.status === 'playing' && !e.repeat) {
        if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
          flap()
        }
      }
      if (s.status === 'gameover' && (e.key === 'r' || e.key === 'R' || e.key === ' ')) {
        startGame()
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
    }
    const onPointerDown = (): void => {
      audio.init()
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready') {
        startGame()
        setTimeout(() => flap(), 30)
        return
      }
      if (s.status === 'playing') {
        flap()
      }
      if (s.status === 'gameover') {
        startGame()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    canvas.addEventListener('pointerdown', onPointerDown)

    const killDrone = (cause: DeathCause): void => {
      const s = stateRef.current
      if (!s) return
      if (s.drone.dead) return
      s.drone.dead = true
      s.drone.deathFrames = 60
      s.shake = 10
      if (cause === 'thud') audio.thud()
      else audio.splash()
      // Death particles: dark smoke + electrical sparks (drone failure).
      for (let i = 0; i < 24; i++) {
        const a = rand(0, Math.PI * 2)
        const sp = rand(1, 5)
        s.particles.push({
          x: s.drone.x,
          y: s.drone.y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - 1,
          life: rand(25, 50),
          color:
            i % 3 === 0
              ? 'rgba(50, 50, 60, 0.8)'
              : i % 3 === 1
                ? 'rgba(255, 209, 102, 0.9)'
                : 'rgba(255, 140, 50, 0.8)',
          size: rand(1.5, 3.5),
        })
      }
      s.floaters.push({
        x: s.drone.x,
        y: s.drone.y - 18,
        text: cause === 'thud' ? 'STRUCK' : 'GROUNDED',
        color: '#d94a4a',
        life: 90,
      })
      audio.fail()
    }

    const checkCollision = (): void => {
      const s = stateRef.current
      if (!s) return
      const t = s.drone
      const tLeft = t.x - DRONE_W / 2 + 3
      const tRight = t.x + DRONE_W / 2 - 3
      const tTop = t.y - DRONE_H / 2 + 2
      const tBottom = t.y + DRONE_H / 2 - 2

      if (tBottom >= GROUND_Y) {
        killDrone('splash')
        return
      }
      if (tTop <= CEILING_DEATH_Y) {
        killDrone('splash')
        return
      }
      for (const o of s.obstacles) {
        const oLeft = o.x
        const oRight = o.x + o.width
        if (tRight < oLeft || tLeft > oRight) continue
        if (tTop < o.gapTop || tBottom > o.gapBottom) {
          killDrone('thud')
          return
        }
      }
    }

    let animationId = 0

    const draw = (): void => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++
      s.bgPhase += 0.5

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const scale = Math.min(screenW / W, screenH / H, 2.0)
      const offsetX = (screenW - W * scale) / 2
      const offsetY = (screenH - H * scale) / 2

      let shakeX = 0
      let shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      ctx.fillStyle = '#080e18'
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      if (s.status === 'playing') {
        const t = s.drone
        if (!t.dead) {
          t.vy += GRAVITY
          if (t.vy > MAX_FALL_SPEED) t.vy = MAX_FALL_SPEED
          if (t.vy < MAX_RISE_SPEED) t.vy = MAX_RISE_SPEED
          t.y += t.vy
          // Rotation tracks vertical velocity — nose points where the drone
          // is heading. Faster = steeper tilt.
          const vyN = clamp(t.vy / MAX_FALL_SPEED, -1, 1)
          t.rotation = vyN > 0 ? vyN * 0.9 : vyN * 0.5
          if (t.rotorBoost > 0) t.rotorBoost--
          t.rotorPhase += 0.15
        }

        if (!t.dead) {
          for (const o of s.obstacles) {
            o.x -= SCROLL_SPEED
          }
          for (const o of s.obstacles) {
            if (!o.passed && o.x + o.width < t.x - DRONE_W / 2) {
              o.passed = true
              s.score += SCORE_PER_PAIR
              audio.score()
              s.floaters.push({
                x: t.x,
                y: t.y - 30,
                text: '+1',
                color: '#ffd166',
                life: 30,
              })
            }
          }
          while (s.obstacles.length > 0 && s.obstacles[0].x + s.obstacles[0].width < -50) {
            s.obstacles.shift()
          }
          while (s.obstacles.length < 4) {
            const lastX = s.obstacles[s.obstacles.length - 1].x
            s.obstacles.push(
              makeObstaclePair(lastX + rand(OBSTACLE_SPACING_MIN, OBSTACLE_SPACING_MAX)),
            )
          }
          checkCollision()
        }

        if (t.dead) {
          t.deathFrames--
          // Continue applying gravity so the drone visibly falls.
          t.vy += GRAVITY * 1.5
          if (t.vy > MAX_FALL_SPEED * 1.5) t.vy = MAX_FALL_SPEED * 1.5
          t.y += t.vy
          t.rotation += 0.04
          if (t.deathFrames <= 0) {
            s.status = 'gameover'
            if (s.score > s.hi) {
              s.hi = s.score
              try {
                localStorage.setItem('inventoryDroneHi', String(s.hi))
              } catch {
                // localStorage may be unavailable; ignore.
              }
            }
            setHud(p => ({ ...p, status: 'gameover', hi: s.hi, score: s.score }))
          }
        }

        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.1
          p.vx *= 0.97
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.6
          f.life--
          return f.life > 0
        })

        setHud(prev => {
          if (prev.score === s.score && prev.status === s.status) return prev
          return { ...prev, score: s.score, status: s.status }
        })
      } else if (s.status === 'ready') {
        s.drone.y = H * 0.4 + Math.sin(s.time * 0.05) * 6
        s.drone.rotation = Math.sin(s.time * 0.05) * 0.05
        s.drone.rotorPhase += 0.15
      } else if (s.status === 'gameover') {
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.1
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.6
          f.life--
          return f.life > 0
        })
      }

      drawBackground(ctx, s)
      drawObstacles(ctx, s)
      drawDrone(ctx, s.drone, s.time)

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 30)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 14px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      if (s.status === 'playing' && !s.drone.dead) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'
        ctx.font = 'bold 36px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(String(s.score), W / 2 + 2, 90)
        ctx.fillStyle = '#ffd166'
        ctx.fillText(String(s.score), W / 2, 88)
      }

      ctx.restore()

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', resize)
      canvas.removeEventListener('pointerdown', onPointerDown)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(4, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(4, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            try {
              audioRef.current.init()
            } catch {
              // ignore audio init failures
            }
            if (startGameRef.current) startGameRef.current()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>INVENTORY DRONE</div>
          <div style={styles.overlaySubLarge}>
            scan the yard inventory · stacks above and below · keep the rotors on
          </div>
          <div style={styles.legend}>
            <div style={styles.legendRow}>SPACE / ↑ / W / click : thrust rotors</div>
            <div style={styles.legendRow}>without thrust the drone falls</div>
            <div style={styles.legendRow}>one bump and the drone goes down</div>
          </div>
          <div style={styles.controls}>tap to start</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>DRONE LOST</div>
          <div style={styles.overlaySub}>
            containers scanned: {hud.score}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
drone ${String(Math.floor(Math.random() * 900) + 100)} reported lost in yard
${hud.score} containers scanned before incident
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'recovery team dispatched'}`}
          </pre>
          <div style={styles.controls}>SPACE or click to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default InventoryDrone

// ============= DRAWING =============

function drawBackground(ctx: CanvasRenderingContext2D, s: GameState): void {
  const sky = ctx.createLinearGradient(0, 0, 0, HORIZON_Y)
  sky.addColorStop(0, '#0d1622')
  sky.addColorStop(0.5, '#1a2a44')
  sky.addColorStop(1, '#3a4a68')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, W, HORIZON_Y)

  const bldgScroll = (s.bgPhase * 0.2) % 240
  ctx.fillStyle = 'rgba(15, 25, 40, 0.7)'
  for (let i = -1; i < 4; i++) {
    const cx = i * 240 - bldgScroll + 30
    const h = 50 + (i % 3) * 20
    ctx.fillRect(cx, HORIZON_Y - 40 - h, 70, 40 + h)
    ctx.fillStyle = 'rgba(255, 209, 102, 0.25)'
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        if ((i + r + c) % 5 < 2) {
          ctx.fillRect(cx + 6 + c * 14, HORIZON_Y - 36 - h + r * 14, 4, 6)
        }
      }
    }
    ctx.fillStyle = 'rgba(15, 25, 40, 0.7)'
  }

  const craneScroll = (s.bgPhase * 0.4) % 280
  ctx.fillStyle = 'rgba(25, 40, 60, 0.85)'
  for (let i = -1; i < 4; i++) {
    const cx = i * 280 - craneScroll + 100
    const ch = 200 + (i % 2) * 40
    const cw = 130
    ctx.fillRect(cx, HORIZON_Y - 12 - ch, 5, ch)
    ctx.fillRect(cx + cw - 5, HORIZON_Y - 12 - ch, 5, ch)
    ctx.fillRect(cx + 30, HORIZON_Y - 12 - ch, 4, ch)
    ctx.fillRect(cx + cw - 34, HORIZON_Y - 12 - ch, 4, ch)
    for (let b = 0; b < 4; b++) {
      const by = HORIZON_Y - 12 - ch + ch * (0.2 + b * 0.2)
      ctx.fillRect(cx, by, cw, 2)
    }
    ctx.fillRect(cx - 14, HORIZON_Y - 12 - ch - 4, cw + 28, 6)
    ctx.fillRect(cx - 14, HORIZON_Y - 12 - ch - 10, cw + 28, 4)
    ctx.fillRect(cx + cw / 2 - 18, HORIZON_Y - 12 - ch + 20, 36, 3)
    ctx.fillStyle = 'rgba(255, 209, 102, 0.5)'
    ctx.fillRect(cx + cw / 2 - 6, HORIZON_Y - 12 - ch + 30, 12, 10)
    ctx.fillStyle = 'rgba(25, 40, 60, 0.85)'
  }

  const pavement = ctx.createLinearGradient(0, HORIZON_Y, 0, H)
  pavement.addColorStop(0, '#2a2f38')
  pavement.addColorStop(0.5, '#1c2028')
  pavement.addColorStop(1, '#0f1218')
  ctx.fillStyle = pavement
  ctx.fillRect(0, HORIZON_Y, W, H - HORIZON_Y)

  const lineScroll = (s.bgPhase * 1.5) % 60
  ctx.strokeStyle = 'rgba(201, 166, 72, 0.35)'
  ctx.lineWidth = 1
  ctx.setLineDash([14, 10])
  for (let i = 0; i < 4; i++) {
    const ly = HORIZON_Y + 16 + i * 22
    ctx.beginPath()
    ctx.moveTo(-lineScroll, ly)
    ctx.lineTo(W, ly)
    ctx.stroke()
  }
  ctx.setLineDash([])

  ctx.strokeStyle = 'rgba(255, 209, 102, 0.25)'
  ctx.lineWidth = 1.5
  ctx.setLineDash([20, 16])
  ctx.beginPath()
  ctx.moveTo(-lineScroll, GROUND_Y - 18)
  ctx.lineTo(W, GROUND_Y - 18)
  ctx.stroke()
  ctx.setLineDash([])

  ctx.fillStyle = '#0a0d12'
  ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y)
  const hazardScroll = (s.bgPhase * 1.5) % 16
  for (let x = -hazardScroll; x < W; x += 16) {
    ctx.fillStyle = '#c9a648'
    ctx.beginPath()
    ctx.moveTo(x, GROUND_Y)
    ctx.lineTo(x + 8, GROUND_Y)
    ctx.lineTo(x + 4, GROUND_Y + 6)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = '#1a1a1a'
    ctx.beginPath()
    ctx.moveTo(x + 8, GROUND_Y)
    ctx.lineTo(x + 16, GROUND_Y)
    ctx.lineTo(x + 12, GROUND_Y + 6)
    ctx.lineTo(x + 4, GROUND_Y + 6)
    ctx.closePath()
    ctx.fill()
  }
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
  ctx.fillRect(0, GROUND_Y + 6, W, 1)
}

function drawObstacles(ctx: CanvasRenderingContext2D, s: GameState): void {
  for (const o of s.obstacles) {
    if (o.x + o.width < -10 || o.x > W + 10) continue
    drawHangingCargo(ctx, o)
    drawDockPillar(ctx, o)
  }
}

function drawSingleContainer(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  colorIdx: number,
): void {
  const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72', '#e6c14a', '#c43838']
  const codes = ['LAX', 'NYC', 'ROT', 'SHA', 'SIN', 'HAM']
  const color = colors[colorIdx % colors.length]
  ctx.fillStyle = color
  ctx.fillRect(x, y, w, h)
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(x, y, w, 2)
  ctx.fillStyle = 'rgba(0,0,0,0.4)'
  ctx.fillRect(x, y + h - 2, w, 2)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.6
  for (let i = 1; i < 6; i++) {
    const lx = x + (w / 6) * i
    ctx.beginPath()
    ctx.moveTo(lx, y + 2)
    ctx.lineTo(lx, y + h - 2)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.6)'
  ctx.lineWidth = 1
  ctx.strokeRect(x, y, w, h)
  if (h >= 24) {
    ctx.fillStyle = 'rgba(255,255,255,0.85)'
    ctx.font = 'bold 10px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(codes[colorIdx % codes.length], x + w / 2, y + h / 2)
    ctx.textBaseline = 'alphabetic'
  }
}

function drawHangingCargo(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const cx = o.x + o.width / 2
  const stackBottom = o.gapTop - 6
  const stackTop = 4
  const stackHeight = stackBottom - stackTop
  const containerH = 30
  const numContainers = Math.max(1, Math.floor(stackHeight / containerH))
  const actualContainerH = stackHeight / numContainers

  ctx.strokeStyle = '#2a2a2a'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(cx - 12, 0)
  ctx.lineTo(cx - 12, stackTop)
  ctx.moveTo(cx + 12, 0)
  ctx.lineTo(cx + 12, stackTop)
  ctx.stroke()

  // Spreader bar holding the lowest container of the stack from below.
  ctx.fillStyle = '#5a4520'
  ctx.fillRect(o.x - 4, stackBottom, o.width + 8, 4)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(o.x - 3, stackBottom + 1, o.width + 6, 2)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(o.x - 4, stackBottom, 3, 4)
  ctx.fillRect(o.x + o.width + 1, stackBottom, 3, 4)

  for (let i = 0; i < numContainers; i++) {
    const y = stackTop + i * actualContainerH
    const colorIdx = (o.cargoColorIdx + i) % 6
    drawSingleContainer(ctx, o.x, y, o.width, actualContainerH, colorIdx)
  }
}

function drawDockPillar(ctx: CanvasRenderingContext2D, o: Obstacle): void {
  const stackTop = o.gapBottom
  const stackBottom = GROUND_Y
  const stackHeight = stackBottom - stackTop
  if (stackHeight < 4) return

  const containerH = 30
  const numContainers = Math.max(1, Math.floor(stackHeight / containerH))
  const actualContainerH = stackHeight / numContainers

  ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'
  ctx.fillRect(o.x - 2, GROUND_Y - 1, o.width + 4, 2)

  for (let i = 0; i < numContainers; i++) {
    const y = stackBottom - (i + 1) * actualContainerH
    // Different starting offset so top and bottom stacks don't have identical
    // color sequences when their counts happen to match.
    const colorIdx = (o.cargoColorIdx + 3 + i) % 6
    drawSingleContainer(ctx, o.x, y, o.width, actualContainerH, colorIdx)
  }

  ctx.fillStyle = '#c9a648'
  ctx.fillRect(o.x, stackTop - 1, 4, 2)
  ctx.fillRect(o.x + o.width - 4, stackTop - 1, 4, 2)
}

function drawDrone(ctx: CanvasRenderingContext2D, d: Drone, time: number): void {
  ctx.save()
  ctx.translate(d.x, d.y)
  ctx.rotate(d.rotation)

  const rotorSpeed = d.dead ? 0 : d.rotorBoost > 0 ? 0.9 : 0.45
  d.rotorPhase += rotorSpeed
  const armLen = 16
  const rotorR = 8

  if (d.rotorBoost > 0 && !d.dead) {
    const flashAlpha = d.rotorBoost / 8
    ctx.fillStyle = `rgba(170, 220, 255, ${flashAlpha * 0.5})`
    for (const dx of [-armLen, armLen]) {
      ctx.beginPath()
      ctx.moveTo(dx - 4, DRONE_H / 2 + 1)
      ctx.lineTo(dx + 4, DRONE_H / 2 + 1)
      ctx.lineTo(dx + 2, DRONE_H / 2 + 8)
      ctx.lineTo(dx - 2, DRONE_H / 2 + 8)
      ctx.closePath()
      ctx.fill()
    }
  }

  ctx.strokeStyle = '#3a3a3a'
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(-armLen, 0)
  ctx.lineTo(armLen, 0)
  ctx.stroke()
  ctx.strokeStyle = '#5a5a5a'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(-armLen, 0)
  ctx.lineTo(armLen, 0)
  ctx.stroke()

  for (const armX of [-armLen, armLen]) {
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(armX - 2, -2, 4, 4)
    if (!d.dead) {
      const blurAlpha = rotorSpeed > 0.7 ? 0.55 : 0.35
      ctx.fillStyle = `rgba(180, 200, 230, ${blurAlpha})`
      ctx.beginPath()
      ctx.ellipse(armX, 0, rotorR, 1.5, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = `rgba(120, 140, 170, 0.4)`
      ctx.lineWidth = 0.6
      ctx.beginPath()
      ctx.ellipse(armX, 0, rotorR, 1.5, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.save()
      ctx.translate(armX, 0)
      ctx.rotate(d.rotorPhase)
      ctx.fillStyle = 'rgba(40, 50, 70, 0.6)'
      ctx.fillRect(-rotorR, -0.5, rotorR * 2, 1)
      ctx.restore()
    } else {
      ctx.strokeStyle = '#3a3a3a'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(armX - rotorR, 0)
      ctx.lineTo(armX + rotorR, 0)
      ctx.moveTo(armX, -rotorR + 4)
      ctx.lineTo(armX, rotorR - 4)
      ctx.stroke()
    }
  }

  ctx.fillStyle = '#2a3548'
  ctx.fillRect(-7, -DRONE_H / 2 + 2, 14, DRONE_H - 4)
  ctx.strokeStyle = '#5a6478'
  ctx.lineWidth = 0.8
  ctx.strokeRect(-7, -DRONE_H / 2 + 2, 14, DRONE_H - 4)
  ctx.fillStyle = '#ffd166'
  ctx.fillRect(-6, -DRONE_H / 2 + 3, 12, 1.5)

  if (!d.dead) {
    const t = Math.floor(time / 8) % 3
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = i === t ? '#3a8fd6' : 'rgba(58, 143, 214, 0.3)'
      ctx.fillRect(-4 + i * 3, -1, 2, 2)
    }
  }

  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(-3, DRONE_H / 2 - 2, 6, 4)
  ctx.fillStyle = '#0a0a0a'
  ctx.beginPath()
  ctx.arc(0, DRONE_H / 2 + 1, 2, 0, Math.PI * 2)
  ctx.fill()
  if (!d.dead) {
    ctx.fillStyle = 'rgba(100, 200, 255, 0.6)'
    ctx.fillRect(-1, DRONE_H / 2, 1, 1)
  }

  if (!d.dead) {
    const ledPulse = 0.5 + Math.sin(time * 0.25) * 0.5
    ctx.fillStyle = `rgba(80, 220, 100, ${ledPulse})`
    ctx.fillRect(-1, -DRONE_H / 2, 2, 2)
    ctx.fillStyle = `rgba(80, 220, 100, ${ledPulse * 0.3})`
    ctx.beginPath()
    ctx.arc(0, -DRONE_H / 2 + 1, 4, 0, Math.PI * 2)
    ctx.fill()
  } else {
    ctx.fillStyle = 'rgba(220, 60, 60, 0.8)'
    ctx.fillRect(-1, -DRONE_H / 2, 2, 2)
  }

  ctx.restore()
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: {
    width: '100%',
    height: '100%',
    display: 'block',
    touchAction: 'none',
    cursor: 'pointer',
  },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 24,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 36,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: {
    fontSize: 13,
    letterSpacing: '0.22em',
    color: '#cfd8e8',
    marginBottom: 4,
    maxWidth: 420,
    textAlign: 'center',
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    letterSpacing: '0.14em',
    color: '#cfd8e8',
    marginTop: 8,
  },
  legendRow: { textAlign: 'center' },
  controls: {
    marginTop: 14,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 380,
  },
}
